import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { getDatabase, ref, get, update, onValue } from "firebase/database";
import "./AdminPack.css";

export default function AdminPack({ fgNumber, nodeName, onBack }) {
  const db = getDatabase();

  // ---------------- STATES ----------------
  const noteOptions = [
    "Corrugated Rack",
    "Part Tag & Sticker",
    "DOT/Date Stamp",
    "Urgent Delivery Request",
    "Have NGs",
  ];
  const [selectedNotes, setSelectedNotes] = useState([]);
  const [pendingDelete, setPendingDelete] = useState(false);
  const [staffList, setStaffList] = useState([]);
  const [zoomImage, setZoomImage] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [form, setForm] = useState({
    quantity: "",
    datePack: "",
    timePack: "",
    userName: "",
    note: "",
    releaseNote: "",
    ngMaterial: "",
    ngManufacturing: "",
    dented: "",
    rusted: "",
    other: "",
  });
  const [images, setImages] = useState({
    qrUrl: "",
    checklistUrl: "",
  });

  const [editRemark, setEditRemark] = useState("");
  const [isHaveNGs, setIsHaveNGs] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  
  const [issue, setIssue] = useState({
    message: "",
    reportedBy: "",
    reportedTime: "",
    status: "open"
  });

  // ---------------- BODY SCROLL LOCK ----------------
  useEffect(() => {
    if (zoomImage) {
      const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = "hidden";
      document.body.style.paddingRight = `${scrollBarWidth}px`;
    } else {
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
    }
  }, [zoomImage]);

  // ---------------- DATE/TIME HELPERS ----------------
  const androidToISODate = (val) => {
    if (!val) return "";
    const parts = val.split("/");
    if (parts.length !== 3) return "";
    const [d, m, y] = parts.map((s) => s.padStart(2, "0"));
    return `${y}-${m}-${d}`;
  };

  const isoToAndroidDate = (val) => {
    if (!val) return "";
    const [y, m, d] = val.split("-");
    return `${d}/${m}/${y}`;
  };

  const androidTimeTo24 = (val) => {
    if (!val) return "";
    const [time, modifier] = val.split(" ");
    if (!time) return val;
    let [h, mm] = time.split(":");
    let hour = parseInt(h, 10);
    if (modifier?.toLowerCase() === "pm" && hour < 12) hour += 12;
    if (modifier?.toLowerCase() === "am" && hour === 12) hour = 0;
    return `${hour.toString().padStart(2, "0")}:${mm}`;
  };

  const time24ToAndroid = (val) => {
    if (!val) return "";
    const [h, mm] = val.split(":");
    let hour = parseInt(h, 10);
    const modifier = hour >= 12 ? "pm" : "am";
    hour = hour % 12 || 12;
    return `${hour.toString().padStart(2, "0")}:${mm} ${modifier}`;
  };

  // ---------------- FETCH DATA ----------------
  useEffect(() => {
    if (!fgNumber || !nodeName) return;

    const nodeRef = ref(db, `${nodeName}/${fgNumber}`);
    const staffRef = ref(db, "Staff");

    const fetchData = async () => {
      try {
        const snap = await get(nodeRef);
        if (snap.exists()) {
          const data = snap.val();
          
          // 1. Prepare form state
          const newForm = {
            quantity: data.quantity || "",
            datePack: data.datePack ? androidToISODate(data.datePack) : "",
            timePack: data.timePack ? androidTimeTo24(data.timePack) : "",
            userName: data.userName || "",
            note: data.note || "",
            releaseNote: data.releaseNoteNo || "",
            ngMaterial: data.NGMaterial || "",
            ngManufacturing: data.NGManufacturing || "",
            dented: data.Dented || "",
            rusted: data.Rusted || "",
            other: data.Other || "",
          };
          setForm(newForm);

          // 2. Set Images
          setImages({
            qrUrl: data.qrImageUrl || "",
            checklistUrl: data.imageUrl || "",
          });

          // 3. Logic to show NG Card if data exists
          const hasNGData = !!(
            data.releaseNoteNo || 
            data.ngMaterial || 
            data.ngManufacturing || 
            data.dented || 
            data.rusted ||
            data.other 
          );

          // 4. Handle Selected Notes
          let parsedNotes = (data.note || "").split(",").map((n) => n.trim()).filter(Boolean);
          
          if (hasNGData) {
            setIsHaveNGs(true);
            // Ensure "Have NGs" is visually active in the buttons if data exists
            if (!parsedNotes.includes("Have NGs")) {
              parsedNotes.push("Have NGs");
            }
          } else {
            setIsHaveNGs(parsedNotes.includes("Have NGs"));
          }

          setSelectedNotes(parsedNotes);
        }

        const staffSnap = await get(staffRef);
        if (staffSnap.exists()) {
          const staff = [];
          staffSnap.forEach((child) => {
            const name = child.val().name;
            if (name) staff.push(name);
          });
          setStaffList(staff);
        }
      } catch (err) {
        console.error("Error fetching data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [fgNumber, nodeName, db]);

  // ---------------- UPDATE FORM NOTE ----------------
  useEffect(() => {
    if (pendingDelete) {
      setForm((prev) => ({ ...prev, note: "" }));
    } else {
      // Logic to sync the actual note string with active buttons
      const displayNotes = selectedNotes.filter(n => n !== "Have NGs");
      setForm((prev) => ({ ...prev, note: displayNotes.join("\n") }));
    }
  }, [selectedNotes, pendingDelete]);

  // ---------------- HANDLERS ----------------
  const toggleNote = (note) => {
    setPendingDelete(false);
    if (note === "Have NGs") {
      setIsHaveNGs(!isHaveNGs);
    }
    setSelectedNotes((prev) =>
      prev.includes(note) ? prev.filter((n) => n !== note) : [...prev, note]
    );
  };

  const handleDeleteNote = () => {
    if (window.confirm("Are you sure you want to delete all notes and NG data?")) {
      setPendingDelete(true);
      setSelectedNotes([]);
      setIsHaveNGs(false);
      
      // Clear the form state using the correct keys defined in your useState
      setForm((prev) => ({
        ...prev,
        note: "",
        releaseNote: "", 
        ngMaterial: "", 
        ngManufacturing: "", 
        dented: "", 
        rusted: "", 
        other: "",
      }));
    }
  };

    const handleSave = async () => {
      if (!fgNumber || !nodeName || isSaving) return;
      setIsSaving(true);

      const rootRef = ref(db);
      const now = Date.now();
      const mainPath = `${nodeName}/${fgNumber}`;
      const updates = {};

      // 1. Core Metadata & Info
      updates[`${mainPath}/quantity`] = form.quantity;
      updates[`${mainPath}/datePack`] = form.datePack ? isoToAndroidDate(form.datePack) : "";
      updates[`${mainPath}/timePack`] = form.timePack ? time24ToAndroid(form.timePack) : "";
      updates[`${mainPath}/userName`] = form.userName;
      updates[`${mainPath}/name`] = form.userName; // Kotlin parity
      updates[`${mainPath}/place`] = "Pack"; // Kotlin parity
      updates[`${mainPath}/timestamp`] = now;

      // 2. Note & NG Conditional Logic
      if (pendingDelete) {
        // Explicitly nullify all related fields for a clean wipe
        updates[`${mainPath}/note`] = null;
        updates[`${mainPath}/releaseNoteNo`] = null;
        updates[`${mainPath}/NGMaterial`] = null;
        updates[`${mainPath}/NGManufacturing`] = null;
        updates[`${mainPath}/Dented`] = null;
        updates[`${mainPath}/Rusted`] = null;
        updates[`${mainPath}/Other`] = null;
      } else {
        // Filter out the "Have NGs" button label from the actual stored note string
        const filteredNotes = selectedNotes.filter((n) => n !== "Have NGs");
        updates[`${mainPath}/note`] = filteredNotes.join(", ");

        if (isHaveNGs) {
          updates[`${mainPath}/releaseNoteNo`] = form.releaseNote;
          updates[`${mainPath}/NGMaterial`] = form.ngMaterial;
          updates[`${mainPath}/NGManufacturing`] = form.ngManufacturing;
          updates[`${mainPath}/Dented`] = form.dented;
          updates[`${mainPath}/Rusted`] = form.rusted;
          updates[`${mainPath}/Other`] = form.other;
        } else {
          // If "Have NGs" is toggled off, clear those specific fields
          updates[`${mainPath}/releaseNoteNo`] = null;
          updates[`${mainPath}/NGMaterial`] = null;
          updates[`${mainPath}/NGManufacturing`] = null;
          updates[`${mainPath}/Dented`] = null;
          updates[`${mainPath}/Rusted`] = null;
          updates[`${mainPath}/Other`] = null;
        }
      }

      // 3. Issue Closing Logic
      if (editRemark.trim()) {
        const issuePath = `Pack/${fgNumber}/issue`;
        updates[`${issuePath}/status`] = "closed";
        updates[`${issuePath}/remark`] = editRemark.trim();
        updates[`${issuePath}/resolvedBy`] = form.userName || "Admin";
        updates[`${issuePath}/resolvedTime`] = now;
      }

      // 4. Progress Sync
      updates[`Progress/${fgNumber}/s4`] = true;

      try {
        // Execute all updates across different nodes in one single atomic operation
        await update(rootRef, updates);
        
        setPendingDelete(false);
        if (onBack) onBack();
      } catch (err) {
        console.error("Save failed:", err);
        alert(`Save error: ${err.message}`);
      } finally {
        setIsSaving(false);
      }
    };

  const handleDeleteFG = async () => {
    if (!fgNumber) return;
    setIsDeleting(true); 
    const updates = {
      [`Pack/${fgNumber}`]: null,
      [`Progress/${fgNumber}/s4`]: null,
      [`Receive/${fgNumber}/locationTag`]: "Paint"
    };
    try {
      await update(ref(db), updates);
      setTimeout(() => onBack && onBack(), 1000);
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setIsDeleting(false); 
    }
  };

  useEffect(() => {
    if (!fgNumber) return;
    const issueRef = ref(db, `Pack/${fgNumber}/issue`);
    const unsubscribe = onValue(issueRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        setIssue(data);
        setEditRemark(data.remark || ""); 
      }
    });
    return () => unsubscribe();
  }, [fgNumber, db]);

  const formatTime = (timestamp) => {
    if (!timestamp) return "-";
    return new Date(timestamp).toLocaleString();
  };

  if (loading) return <p>Loading...</p>;

  return (
    <div className="admin-pack-root">
      <div className="card">
        <h2 className="title">{nodeName} – {fgNumber}</h2>
        
        {issue?.message && (
          <div className="issue-card">
            <div className="issue-header">
              <div className="issue-title">⚠ Issue Reported</div>
              <span className={`issue-status ${issue.status}`}>
                {issue.status?.toUpperCase() || "UNKNOWN"}
              </span>
            </div>
            <div className="issue-message">{issue.message}</div>
            <div className="issue-meta">
              <span>👤 {issue.reportedBy || "-"}</span>
              <span>🕒 {formatTime(issue.reportedTime)}</span>
            </div>
            {issue.status === "closed" && (
              <div className="issue-resolved-info">
                Resolved by: {issue.resolvedBy} | {formatTime(issue.resolvedTime)}
              </div>
            )}
            <textarea
              className="remark-editor"
              value={editRemark}
              placeholder="Enter or edit admin remark"
              onChange={(e) => setEditRemark(e.target.value)}
            />
          </div>
        )}

        <button className="delete-fg-btn" onClick={handleDeleteFG}>Delete Pack 🗑</button>

        <div className="images-row">
          <div className="image-box" onClick={() => setZoomImage(images.checklistUrl)}>
            <img src={images.checklistUrl} alt="Checklist" />
            <div className="image-label">Checklist</div>
          </div>
        </div>

        {zoomImage && createPortal(
          <div className="image-zoom-overlay" onClick={() => setZoomImage(null)}>
            <div className="image-zoom-content">
              <img src={zoomImage} alt="Zoomed" />
            </div>
          </div>,
          document.body
        )}

        <div className="form">
          <label className="form-row">
            <span className="label">Current Note:</span>
            <textarea readOnly value={form.note} className="current-note" />
          </label>

          <label className="form-row">
            <span className="label">New Note:</span>
            <div className="notes-controls">
              <div className="note-options">
                {noteOptions.map((note, idx) => (
                  <button
                    key={idx}
                    className={`note-btn ${note === "Have NGs" ? (isHaveNGs ? "active" : "") : (selectedNotes.includes(note) ? "active" : "")}`}
                    onClick={() => toggleNote(note)}
                  >
                    {note}
                  </button>
                ))}
                <button className="delete-note-btn" onClick={handleDeleteNote}>🗑 Delete Note</button>
              </div>
            </div>
          </label>

          {/* This is the card you are looking for */}
          {isHaveNGs && (
            <div className="card ng-card">
              <h3>NG Details</h3>
              {["releaseNote", "ngMaterial", "ngManufacturing", "dented", "rusted", "other"].map((field) => (
                <div key={field} className="ng-form-row">
                  <span className="label" style={{textTransform: 'capitalize'}}>{field.replace(/([A-Z])/g, ' $1')}:</span>
                  <input type="text" value={form[field]} onChange={(e) => setForm({ ...form, [field]: e.target.value })} />
                </div>
              ))}
            </div>
          )}

          <div className="date-time-row">
            <label className="label">Quantity:</label>
            <input type="number" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} />
          </div>

          <div className="date-time-row">
            <label className="label">Pack Date:</label>
            <input type="date" value={form.datePack} onChange={(e) => setForm({ ...form, datePack: e.target.value })} />
          </div>

          <div className="date-time-row">
            <label className="label">Pack Time:</label>
            <input type="time" value={form.timePack} onChange={(e) => setForm({ ...form, timePack: e.target.value })} />
          </div>

          <label className="form-row">
            <span className="label">User:</span>
            <select value={form.userName} onChange={(e) => setForm({ ...form, userName: e.target.value })}>
              <option value="">Select User</option>
              {staffList.map((s, idx) => <option key={idx} value={s}>{s}</option>)}
            </select>
          </label>

          <div className="actions">
            <button className="btn-back" onClick={() => onBack && onBack()}>⬅ Back</button>
            <button className="btn-save" onClick={handleSave}>💾 Save</button>
          </div>
        </div>
      </div>
      {isDeleting && (
        <div className="loading-overlay">
          <div className="spinner"></div>
          <p>Deleting FG Number...</p>
        </div>
      )}
    </div>
  );
}